import React from "react";
import { useTranslation } from "react-i18next";
import DemoPreviewCard from "./DemoPreviewCard";
import "./HeroSection.css";

interface HeroSectionProps {
  isLive: boolean;
  onSignUp: () => void;
  onOpenDemo: () => void;
  isLocalhost: boolean;
}

const HeroSection: React.FC<HeroSectionProps> = ({
  isLive,
  onSignUp,
  onOpenDemo,
  isLocalhost,
}) => {
  const { t, ready, i18n } = useTranslation("marketing");

  // Guard against rendering before translations are loaded.
  // The hero section is eagerly rendered and can beat the HTTP backend.
  if (!ready || !i18n.hasLoadedNamespace("marketing")) return null;

  return (
    <section className="hero-section" aria-label={t("landing.hero.badge")}>
      {/* CSS-only background treatment */}
      <div className="hero-section__background" aria-hidden="true" />

      <div className="hero-section__content">
        {/* Text content */}
        <div className="hero-section__text">
          <div className="hero-section__badge">
            <span className="hero-section__badge-dot" aria-hidden="true" />
            {t("landing.hero.badge")}
          </div>

          <h1 className="hero-section__title">
            {t("landing.hero.title")}
            <br />
            <span className="hero-section__title-highlight">
              {t("landing.hero.titleHighlight")}
            </span>
          </h1>

          <p className="hero-section__subtitle">
            {t("landing.hero.subtitle")}
          </p>

          <div className="hero-section__cta-group">
            {isLive ? (
              <>
                <button
                  className="hero-section__cta-primary"
                  onClick={onSignUp}
                  aria-label={t("landing.hero.ctaPrimaryAriaLabel")}
                >
                  {t("landing.hero.ctaPrimary")}
                </button>
                <button
                  className="hero-section__cta-secondary"
                  onClick={onOpenDemo}
                  aria-label={t("landing.hero.ctaSecondaryAriaLabel")}
                >
                  {t("landing.hero.ctaSecondary")}
                </button>
              </>
            ) : (
              <button
                className="hero-section__cta-primary"
                disabled
                aria-label={t("landing.hero.ctaComingSoon")}
              >
                {t("landing.hero.ctaComingSoon")}
              </button>
            )}
          </div>

          <div className="hero-section__trust">
            <div className="hero-section__trust-item">
              <span className="hero-section__trust-icon" aria-hidden="true">
                ✓
              </span>
              <span>{t("landing.hero.trust.noCreditCard")}</span>
            </div>
            <div className="hero-section__trust-item">
              <span className="hero-section__trust-icon" aria-hidden="true">
                ✓
              </span>
              <span>{t("landing.hero.trust.freeToStart")}</span>
            </div>
            <div className="hero-section__trust-item">
              <span className="hero-section__trust-icon" aria-hidden="true">
                ✓
              </span>
              <span>{t("landing.hero.trust.skillCount")}</span>
            </div>
          </div>
        </div>

        {/* Demo preview card */}
        <DemoPreviewCard onClick={onOpenDemo} isLocalhost={isLocalhost} />
      </div>
    </section>
  );
};

export default HeroSection;
